import { useMemo } from "react";
import { useFilesStore } from "./files-store";

export type NodeKind = "document" | "folder" | "template" | "meeting" | "task";

export interface GraphNode {
  id: string;
  name: string;
  group: number;
  project?: string;
  kind?: NodeKind;
  [key: string]: unknown;
}

export interface GraphLink {
  source: string;
  target: string;
  direction?: "forward" | "backward" | "bidirectional";
  [key: string]: unknown;
}

export interface GraphData {
  nodes: GraphNode[];
  links: GraphLink[];
}

/**
 * Mapa de colores por tipo de nodo. Se usa en el render del grafo.
 * Mantener sincronizado con `getNodeColor` en `ForceGraphCanvas`.
 */
export const NODE_KIND_COLORS: Record<NodeKind, string> = {
  document: "#ef4444", // red-500 (primary)
  folder: "#f59e0b", // amber-500
  template: "#8b5cf6", // violet-500
  meeting: "#3b82f6", // blue-500
  task: "#10b981", // emerald-500
};

export function useGraphData(
  selectedProject: string,
  selectedMember: string,
  selectedTimeframe: "all" | "week" | "month"
): GraphData {
  const files = useFilesStore((s) => s.files);

  return useMemo(() => {
    let filteredFiles = files.filter((f) => f.type === "file");

    if (selectedProject !== "all") {
      filteredFiles = filteredFiles.filter((f) => f.project === selectedProject);
    }

    const fileIds = new Set(filteredFiles.map((f) => f.id));

    const nodes: GraphNode[] = filteredFiles.map((f) => {
      let group = 1;
      if (f.project === "Wiki Arsenal") group = 1;
      else if (f.project === "Recursos Humanos") group = 2;
      else if (f.project === "Diseño UI/UX") group = 3;
      else if (f.project === "Seguridad") group = 4;
      else if (f.project === "Estrategia") group = 5;
      return { id: f.id, name: f.name.replace(".md", ""), group, project: f.project };
    });

    const links: GraphLink[] = [];
    const findFileByName = useFilesStore.getState().findFileByName;
    filteredFiles.forEach((file) => {
      if (!file.content) return;
      const regex = /\[\[([^\]]+)\]\]/g;
      let match;
      const seen = new Set<string>();
      while ((match = regex.exec(file.content)) !== null) {
        const targetName = match[1].trim();
        const targetFile = findFileByName(targetName);
        if (targetFile && targetFile.id !== file.id && !seen.has(targetFile.id)) {
          seen.add(targetFile.id);
          if (fileIds.has(targetFile.id)) {
            links.push({ source: file.id, target: targetFile.id });
          }
        }
      }
    });

    return { nodes, links };
  }, [files, selectedProject, selectedMember, selectedTimeframe]);
}